import { Injectable } from "@angular/core";
import { BaseApiService } from "../base-api.service";
import { HttpClient } from "@angular/common/http";
import { delay, firstValueFrom, map, Observable } from "rxjs";
import { environment } from "@beyou/environments/environment";
import { MessageTemplate, MessageTemplateBase, MessageTemplateTranslation } from "../../models/message-template.model";
import { MessageTemplateMapper } from "../../mappers/message-templates/message-template.mapper";
import { MessageTemplateDto } from "../../dto/message-templates/message-template.dto";
import { MessageTemplateTranslationsDto } from "../../dto/message-templates/message-template-translations.dto";
import { MessageTemplateTranslationMapper } from "../../mappers/message-templates/message-template-translation.mapper";
import { ExportResultDto } from "../../dto/export-result.dto";

@Injectable({
  providedIn: 'root'
})
export class MessageTemplateApiService extends BaseApiService {

  constructor(http: HttpClient) {
    super(http);
  }

  public getAll(): Observable<MessageTemplate[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/message-templates`;
    return this.getAllPagedData<MessageTemplateDto>(apiUrl).pipe(
      map((response) => {
        return response.map(item => {
          return MessageTemplateMapper.toModel(item);
        })
      })
    );
  }

  public async add(messageTemplate: MessageTemplateBase): Promise<MessageTemplate> {
    const apiUrl = `${environment.adminApiBaseUrl}/message-templates`;
    const dto = MessageTemplateMapper.toCreateDto(messageTemplate);
    console.debug('new message template dto', dto);
    return await firstValueFrom(this.http.post<MessageTemplateDto>(apiUrl, dto).pipe(
      map((response) => {
        console.debug('message template added: ', response.id);
        return MessageTemplateMapper.toModel(response);
      })
    ));
  }

  public async update(messageTemplate: MessageTemplate): Promise<MessageTemplate> {
    const apiUrl = `${environment.adminApiBaseUrl}/message-templates/${messageTemplate.id}`;
    const dto = MessageTemplateMapper.toUpdateDto(messageTemplate);
    return await firstValueFrom(this.http.put<MessageTemplateDto>(apiUrl, dto).pipe(
      map((response) => {
        console.debug('message template updated: ', response.id);
        return MessageTemplateMapper.toModel(response);
      })
    ));
  }

  public async delete(id: number): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/message-templates/${id}`;
    try {
      await firstValueFrom(this.http.delete(apiUrl));
      console.log('Message template deleted: ', id);
    } catch (error) {
      console.error('Error deleting message template with id: ', id, error);
      throw error;
    }
  }

  public async export(languageId: number): Promise<ExportResultDto> {
    const apiUrl = `${environment.adminApiBaseUrl}/message-templates/export?language_id=${languageId}`;
    return await firstValueFrom(this.http.get<ExportResultDto>(apiUrl));
  }

  public async import(file: File): Promise<void> {
    const uploadData = new FormData();
    uploadData.append('file', file);
    const apiUrl = `${environment.adminApiBaseUrl}/message-templates/import`;
    await firstValueFrom(this.http.post(apiUrl, uploadData));
  }

  public async getTranslations(id: number): Promise<MessageTemplateTranslation[]> {
    const apiUrl = `${environment.adminApiBaseUrl}/message-templates/${id}/translations`;
    return await firstValueFrom(this.http.get<MessageTemplateTranslationsDto>(apiUrl).pipe(
      map((response) => {
        console.debug('message template translations retrieved: ', id);
        return response.translations.map(item => MessageTemplateTranslationMapper.toModel(item));
      })
    ));
  }

  public async updateTranslations(id: number, translations: MessageTemplateTranslation[]): Promise<void> {
    const apiUrl = `${environment.adminApiBaseUrl}/message-templates/${id}/translations`;
    const dto = {
      translations: translations.map(item => MessageTemplateTranslationMapper.toDto(item))
    };
    await firstValueFrom(this.http.post(apiUrl, dto));
    console.debug('message template translations updated: ', id);
  }
}
